import React, { useState, useEffect } from 'react';
import { Send, MapPin, Building2, Eye, Mail, Phone, Car, User, Calendar, Megaphone, CheckCircle, Paintbrush, Clock } from 'lucide-react';
import { MapContainer, Circle as LeafletCircle, Marker, Polygon, Popup, useMapEvents } from 'react-leaflet';
import { OpenStreetMapTiles } from '../components/OpenStreetMapTiles';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { CarPreview } from '../components/Car3DViewer';
import { I270_POLYGON } from '../lib/i270';
import { isValidPolygon, safePolygonPositions } from '../lib/leaflet-helpers';
import { unionAreaSqMi, maxRadiusWithinBudget } from '../lib/zone-area';
import { CAMPAIGN_DRIVER_OPTIONS, MAX_CAMPAIGN_DRIVERS } from '../lib/campaign';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Columbus metropolitan area boundary (approximate polygon)
const COLUMBUS_METRO_BOUNDS = {
  north: 40.25,
  south: 39.65,
  east: -82.65,
  west: -83.35,
  center: { lat: 40.00, lng: -83.00 }
};

// Check if a point is within Columbus metro
const isPointInColumbus = (lat, lng) => {
  return (
    lat >= COLUMBUS_METRO_BOUNDS.south &&
    lat <= COLUMBUS_METRO_BOUNDS.north &&
    lng >= COLUMBUS_METRO_BOUNDS.west &&
    lng <= COLUMBUS_METRO_BOUNDS.east
  );
};

// Check if entire circle is within Columbus metro (check 8 points on circumference)
const isCircleInColumbus = (centerLat, centerLng, radiusMiles) => {
  const radiusKm = radiusMiles * 1.60934;
  const radiusDegrees = radiusKm / 111;
  
  if (!isPointInColumbus(centerLat, centerLng)) return false;
  
  for (let i = 0; i < 8; i++) {
    const angle = (i * 45) * (Math.PI / 180);
    const lat = centerLat + radiusDegrees * Math.cos(angle);
    const lng = centerLng + radiusDegrees * Math.sin(angle) / Math.cos(centerLat * Math.PI / 180);
    
    if (!isPointInColumbus(lat, lng)) return false;
  }
  
  return true;
};

// Map click handler component
const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
  });
  return null;
};

const COLUMBUS_CENTER = { lat: 40.00, lng: -83.00 }; // Approximate center of I-270 loop
const MAX_MINI_ZONES = 9;
const TOTAL_AREA_BUDGET = 30; // 30 sq miles
const DEFAULT_ZONE_RADIUS = 1.0; // 1 mile default
const MIN_ZONE_RADIUS = 0.25;
const MAX_ZONE_RADIUS = 3.0;

const LeadForm = () => {
  const [activeTab, setActiveTab] = useState('advertiser');
  
  // Advertiser form state
  const [advertiserData, setAdvertiserData] = useState({
    companyName: '',
    email: '',
    phone: '',
    impressions: '',
    campaignDuration: '',
    wrapType: '',
    designPreference: '',
    driverCount: '',
    instagramHandle: '',
  });
  const [miniZones, setMiniZones] = useState([]);
  const [mapError, setMapError] = useState('');
  const [advertiserStatus, setAdvertiserStatus] = useState(null);
  const [isSubmittingAdvertiser, setIsSubmittingAdvertiser] = useState(false);

  // Driver form state
  const [driverData, setDriverData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    zoneCenterLat: null,
    zoneCenterLng: null,
    carMake: '',
    carModel: '',
    carYear: '',
  });
  const [driverStatus, setDriverStatus] = useState(null);
  const [isSubmittingDriver, setIsSubmittingDriver] = useState(false);
  const [driverMapError, setDriverMapError] = useState('');
  const [geofences, setGeofences] = useState([]);
  const [showImpressionsHr, setShowImpressionsHr] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/geofences`)
      .then(r => r.ok ? r.json() : { geofences: [] })
      .then(d => setGeofences(d.geofences || []))
      .catch(() => {});
  }, []);

  // Advertiser form handlers
  const handleAdvertiserChange = (e) => {
    const { name, value } = e.target;
    setAdvertiserData(prev => {
      const next = { ...prev, [name]: value };
      // Testimonial Partial Wrap keeps its 3-month term; the selected fleet size stays unchanged.
      if (name === 'wrapType' && value === 'testimonial_partial_wrap') {
        next.campaignDuration = '3';
      }
      return next;
    });
  };

  // Total area used by all mini zones — overlap counted once, matching the server.
  const getTotalArea = (zones) => unionAreaSqMi(zones);
  
  const totalAreaUsed = getTotalArea(miniZones);
  const areaRemaining = TOTAL_AREA_BUDGET - totalAreaUsed;

  // Advertiser map click handler - add mini zones
  const handleMapClick = (latlng) => {
    const { lat, lng } = latlng;
    
    // Check if click is within the 9-mile zone
    const distKm = Math.sqrt(
      Math.pow((lat - COLUMBUS_CENTER.lat) * 111, 2) + 
      Math.pow((lng - COLUMBUS_CENTER.lng) * 111 * Math.cos(COLUMBUS_CENTER.lat * Math.PI / 180), 2)
    );
    const distMiles = distKm / 1.60934;
    
    if (distMiles > 9) {
      setMapError('Mini zones must be placed within the 9-mile Columbus zone.');
      return;
    }
    
    if (miniZones.length >= MAX_MINI_ZONES) {
      setMapError(`Maximum ${MAX_MINI_ZONES} mini zones allowed.`);
      return;
    }
    
    // Largest zone that still fits the remaining budget, with overlap counted once
    const maxPossibleRadius = maxRadiusWithinBudget(miniZones, -1, { lat, lng }, TOTAL_AREA_BUDGET);
    if (maxPossibleRadius < MIN_ZONE_RADIUS) {
      setMapError('No area budget remaining. Shrink existing zones or remove one to add more.');
      return;
    }
    const newRadius = Math.min(DEFAULT_ZONE_RADIUS, maxPossibleRadius);
    
    setMiniZones(prev => [...prev, { id: (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`), lat, lng, radius: newRadius }]);
    setMapError('');
  };
  
  const updateZoneRadius = (index, newRadius) => {
    setMiniZones(prev => {
      const updated = [...prev];
      const maxRadius = maxRadiusWithinBudget(prev, index, prev[index], TOTAL_AREA_BUDGET);
      updated[index] = { ...updated[index], radius: Math.min(newRadius, maxRadius) };
      return updated;
    });
  };
  
  const removeZone = (index) => {
    setMiniZones(prev => prev.filter((_, i) => i !== index));
    setMapError('');
  };
  
  const removeLastZone = () => {
    setMiniZones(prev => prev.slice(0, -1));
    setMapError('');
  };
  
  const clearAllZones = () => {
    setMiniZones([]);
    setMapError('');
  };

  const handleAdvertiserSubmit = async (e) => {
    e.preventDefault();
    
    if (miniZones.length === 0) {
      setMapError('Please click on the map to place at least one mini zone.');
      return;
    }

    if (!advertiserData.driverCount) {
      setMapError('Please select how many drivers you want.');
      return;
    }

    setIsSubmittingAdvertiser(true);
    setAdvertiserStatus(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/advertisers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: advertiserData.companyName,
          email: advertiserData.email,
          phone: advertiserData.phone,
          campaign_duration: advertiserData.campaignDuration,
          center_lat: COLUMBUS_CENTER.lat,
          center_lng: COLUMBUS_CENTER.lng,
          mini_zones: miniZones,
          wrap_type: advertiserData.wrapType,
          driver_count: parseInt(advertiserData.driverCount),
          instagram_handle: (advertiserData.instagramHandle || '').trim().replace(/^@/, ''),
        }),
      });

      if (response.ok) {
        setAdvertiserStatus('success');
        setAdvertiserData({
          companyName: '',
          email: '',
          phone: '',
          impressions: '',
          campaignDuration: '',
          wrapType: '',
          designPreference: '',
          driverCount: '',
          instagramHandle: '',
        });
        setMiniZones([]);
      } else if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        setMapError(data.detail || 'A campaign already exists for this email.');
        setAdvertiserStatus('duplicate');
      } else {
        setAdvertiserStatus('error');
      }
    } catch (error) {
      console.error('Error submitting campaign request:', error);
      setAdvertiserStatus('error');
    } finally {
      setIsSubmittingAdvertiser(false);
    }
  };

  // Driver form handlers
  const handleDriverChange = (e) => {
    const { name, value } = e.target;
    setDriverData(prev => ({ ...prev, [name]: value }));
  };

  const handleDriverSubmit = async (e) => {
    e.preventDefault();
    
    if (!driverData.zoneCenterLat || !driverData.zoneCenterLng) {
      setDriverMapError('Please select your driving zone on the map.');
      return;
    }
    
    setIsSubmittingDriver(true);
    setDriverStatus(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/drivers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: driverData.name,
          email: driverData.email,
          phone: driverData.phone,
          date_of_birth: driverData.dateOfBirth,
          zone_center_lat: driverData.zoneCenterLat,
          zone_center_lng: driverData.zoneCenterLng,
          car_make: driverData.carMake,
          car_model: driverData.carModel,
          car_year: driverData.carYear,
        }),
      });

      if (response.ok) {
        setDriverStatus('success');
        setDriverData({
          name: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          zoneCenterLat: null,
          zoneCenterLng: null,
          carMake: '',
          carModel: '',
          carYear: '',
        });
      } else {
        setDriverStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setDriverStatus('error');
    } finally {
      setIsSubmittingDriver(false);
    }
  };

  return (
    <div className="bg-cream-light" data-testid="lead-form-page">
      {/* Hero */}
      <section className="hero-gradient py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4">
            Get Started
          </h1>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white border-b border-navy/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex">
            <button
              onClick={() => setActiveTab('advertiser')}
              className={`flex-1 py-4 px-6 text-center font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'advertiser'
                  ? 'text-navy border-b-4 border-navy bg-cream/50'
                  : 'text-navy/60 hover:text-navy hover:bg-cream/30'
              }`}
              data-testid="tab-advertiser"
            >
              <Megaphone size={20} />
              Start Your Campaign
            </button>
            <button
              onClick={() => setActiveTab('driver')}
              className={`flex-1 py-4 px-6 text-center font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'driver'
                  ? 'text-navy border-b-4 border-navy bg-cream/50'
                  : 'text-navy/60 hover:text-navy hover:bg-cream/30'
              }`}
              data-testid="tab-driver"
            >
              <Car size={20} />
              Sign Up as a Driver
            </button>
          </div>
        </div>
      </section>

      {/* Advertiser Form */}
      {activeTab === 'advertiser' && (
        <section className="py-12 bg-white" data-testid="advertiser-form-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Success State */}
            {advertiserStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-white" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-green-800 mb-2">Campaign Request Submitted!</h2>
                <p className="text-green-700 mb-6">
                  Thank you for your interest! Your campaign request has been submitted and we'll be in touch soon to discuss next steps.
                </p>
                <button
                  onClick={() => setAdvertiserStatus(null)}
                  className="bg-navy text-cream px-6 py-3 rounded-full font-semibold hover:bg-navy-dark transition-all"
                >
                  Submit Another Request
                </button>
              </div>
            )}

            {/* Form */}
            {advertiserStatus !== 'success' && (
              <form onSubmit={handleAdvertiserSubmit} className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Form Fields */}
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-navy mb-6">Campaign Details</h2>
                    
                    {/* Company Name */}
                    <div>
                      <label className="flex items-center gap-2 text-navy font-medium mb-2">
                        <Building2 size={18} />
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={advertiserData.companyName}
                        onChange={handleAdvertiserChange}
                        required
                        className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors"
                        placeholder="Enter your company name"
                        data-testid="company-name-input"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="flex items-center gap-2 text-navy font-medium mb-2">
                        <Mail size={18} />
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={advertiserData.email}
                        onChange={handleAdvertiserChange}
                        required
                        className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors"
                        placeholder="your@email.com"
                        data-testid="advertiser-email-input"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="flex items-center gap-2 text-navy font-medium mb-2">
                        <Phone size={18} />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={advertiserData.phone}
                        onChange={handleAdvertiserChange}
                        required
                        className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors"
                        placeholder="(555) 123-4567"
                        data-testid="advertiser-phone-input"
                      />
                    </div>

                    {/* Campaign Duration */}
                    <div>
                      <label className="flex items-center gap-2 text-navy font-medium mb-2">
                        <Clock size={18} />
                        Campaign Duration
                      </label>
                      <select
                        name="campaignDuration"
                        value={advertiserData.campaignDuration}
                        onChange={handleAdvertiserChange}
                        required
                        disabled={advertiserData.wrapType === 'testimonial_partial_wrap'}
                        className={`w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors bg-white ${advertiserData.wrapType === 'testimonial_partial_wrap' ? 'opacity-60 cursor-not-allowed' : ''}`}
                        data-testid="campaign-duration-input"
                      >
                        <option value="">Select campaign duration</option>
                        <option value="2">2 months</option>
                        <option value="3">3 months</option>
                        <option value="4">4 months</option>
                        <option value="5">5 months</option>
                        <option value="6">6 months</option>
                      </select>
                      {advertiserData.wrapType === 'testimonial_partial_wrap' && (
                        <p className="text-xs text-amber-700 mt-1" data-testid="testimonial-duration-lock">
                          ✓ Testimonial campaigns are a fixed 3-month promo. Length is locked.
                        </p>
                      )}
                    </div>

                    {/* Error Message */}
                    {mapError && (
                      <div className="bg-red-50 border border-red-200 p-4 rounded-xl">
                        <p className="text-red-700">{mapError}</p>
                      </div>
                    )}

                    {advertiserStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 p-4 rounded-xl">
                        <p className="text-red-700">There was an error submitting your request. Please try again.</p>
                      </div>
                    )}

                    {advertiserStatus === 'duplicate' && (
                      <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl" data-testid="advertiser-duplicate-banner">
                        <p className="text-amber-800 font-semibold mb-1">A campaign already exists for this email.</p>
                        <p className="text-amber-700 text-sm">
                          You can only submit one Get Started form per email. <a href="/advertiser-login" className="underline font-semibold">Log in to your dashboard</a> or <a href="/contact" className="underline font-semibold">contact us</a> to update your existing campaign.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Map */}
                  <div>
                    <h2 className="text-2xl font-bold text-navy mb-4">Select Your Premium Zones</h2>
                    <p className="text-navy/70 mb-2">
                      Click on the map to place up to {MAX_MINI_ZONES} premium-rate mini zones within Columbus. Use the sliders to adjust each zone&apos;s size. Total area budget: {TOTAL_AREA_BUDGET} sq mi — where zones overlap, the shared area is only counted once.
                    </p>
                    
                    {/* Area Budget Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-navy/70 mb-1">
                        <span>Area used: {totalAreaUsed.toFixed(1)} sq mi</span>
                        <span>Remaining: {Math.max(0, areaRemaining).toFixed(1)} sq mi</span>
                      </div>
                      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${Math.min(100, (totalAreaUsed / TOTAL_AREA_BUDGET) * 100)}%`,
                            backgroundColor: totalAreaUsed > TOTAL_AREA_BUDGET * 0.9 ? '#ef4444' : '#1e3a5f'
                          }}
                        />
                      </div>
                    </div>

                    {/* Impressions/hr Toggle */}
                    <div className="mb-3 flex items-center justify-between flex-wrap gap-2 bg-cream rounded-xl px-3 py-2 border border-navy/10">
                      <label className="flex items-center gap-2 text-sm text-navy cursor-pointer" data-testid="impressions-hr-toggle">
                        <input
                          type="checkbox"
                          checked={showImpressionsHr}
                          onChange={(e) => setShowImpressionsHr(e.target.checked)}
                          className="w-4 h-4 accent-navy"
                        />
                        <span className="font-semibold">Show impressions/hr</span>
                      </label>
                      <span className="text-xs text-navy/60">
                        {showImpressionsHr
                          ? 'Click a zone to see its impressions/hr. Turn off to place premium zones.'
                          : 'Click the map to place premium zones.'}
                      </span>
                    </div>

                    <div className="rounded-2xl overflow-hidden border-2 border-navy/20" style={{ height: '400px' }} data-testid="lead-form-map">
                      <MapContainer
                        center={[COLUMBUS_CENTER.lat, COLUMBUS_CENTER.lng]}
                        zoom={10}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <OpenStreetMapTiles mapId="lead-form-map" />
                        <MapClickHandler onMapClick={handleMapClick} />

                        {/* Admin-defined geofences (same zones shown on the advertiser dashboard) */}
                        {geofences
                          .filter(isValidPolygon)
                          .map((gf) => (
                          <Polygon
                            key={`gf-${gf.id}`}
                            positions={safePolygonPositions(gf.polygon)}
                            pathOptions={{
                              color: gf.color || '#3388ff',
                              fillColor: gf.color || '#3388ff',
                              fillOpacity: 0.12,
                              weight: 2,
                              interactive: showImpressionsHr,
                            }}
                          >
                            {showImpressionsHr && (
                              <Popup>
                                <div style={{ minWidth: 160 }}>
                                  <p style={{ margin: 0, fontWeight: 700 }}>{gf.name}</p>
                                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>
                                    {(gf.impressions_per_hour || 0).toLocaleString()} impr/hr
                                  </p>
                                </div>
                              </Popup>
                            )}
                          </Polygon>
                        ))}
                        
                        {/* Mini zones placed by advertiser */}
                        {miniZones.map((zone) => (
                          <LeafletCircle
                            key={zone.id}
                            center={[zone.lat, zone.lng]}
                            radius={zone.radius * 1609.34}
                            pathOptions={{
                              color: '#1e3a5f',
                              fillColor: '#1e3a5f',
                              fillOpacity: 0.25,
                              weight: 2,
                            }}
                          />
                        ))}
                      </MapContainer>
                    </div>
                    
                    {/* Zone Sliders */}
                    {miniZones.length > 0 && (
                      <div className="mt-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <p className="text-sm font-semibold text-navy">Adjust Zone Sizes</p>
                          <div className="flex gap-2">
                            <button type="button" onClick={removeLastZone} className="text-xs text-red-500 underline hover:text-red-700">Undo last</button>
                            <button type="button" onClick={clearAllZones} className="text-xs text-red-500 underline hover:text-red-700">Clear all</button>
                          </div>
                        </div>
                        {miniZones.map((zone, index) => {
                          const zoneArea = Math.PI * zone.radius * zone.radius;
                          const otherArea = miniZones.reduce((sum, z, i) => i === index ? sum : sum + Math.PI * z.radius * z.radius, 0);
                          const maxSlider = Math.min(MAX_ZONE_RADIUS, Math.sqrt((TOTAL_AREA_BUDGET - otherArea) / Math.PI));
                          return (
                            <div key={zone.id} className="flex items-center gap-3 bg-cream/50 p-3 rounded-xl">
                              <span className="text-navy font-bold text-sm w-16 shrink-0">Zone {index + 1}</span>
                              <input
                                type="range"
                                min={MIN_ZONE_RADIUS}
                                max={maxSlider}
                                step={0.05}
                                value={zone.radius}
                                onChange={(e) => updateZoneRadius(index, parseFloat(e.target.value))}
                                className="flex-1 accent-navy"
                                data-testid={`zone-slider-${index}`}
                              />
                              <span className="text-navy/70 text-xs w-24 shrink-0 text-right">{zone.radius.toFixed(2)} mi ({zoneArea.toFixed(1)} sq mi)</span>
                              <button type="button" onClick={() => removeZone(index)} className="text-red-400 hover:text-red-600 text-sm shrink-0">&times;</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {miniZones.length === 0 && (
                      <p className="text-sm text-navy/60 mt-2">
                        <MapPin className="inline" size={14} /> Click the map to place your premium-rate mini zones
                      </p>
                    )}
                  </div>
                </div>

                {/* Design & Impressions Section */}
                <div className="mt-10 pt-8 border-t border-navy/10">
                  <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-2">
                    <Paintbrush size={24} />
                    Design & Impressions
                  </h2>
                  <p className="text-navy/70 mb-6">
                    Choose your wrap type and how many cars you want on the road.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                    {/* Wrap Type Dropdown */}
                    <div className="flex flex-col">
                      <label className="flex items-center gap-2 text-navy font-medium mb-2">
                        <Car size={18} />
                        Wrap Type
                      </label>
                      <p className="text-xs text-navy/50 mb-2">
                        Wrap design is handled by professional in-house designers at our partnered wrap stores.
                      </p>
                      <select
                        name="wrapType"
                        value={advertiserData.wrapType}
                        onChange={handleAdvertiserChange}
                        required
                        className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors bg-white"
                        data-testid="wrap-type-select"
                      >
                        <option value="">Select wrap type</option>
                        <option value="full_wrap">Full Wrap</option>
                        <option value="partial_wrap">Partial Wrap</option>
                        <option value="testimonial_partial_wrap">Testimonial Partial Wrap</option>
                      </select>
                      {advertiserData.wrapType === 'testimonial_partial_wrap' && (
                        <p className="text-xs text-amber-700 mt-1" data-testid="testimonial-per-driver-rate">
                          $681/mo per driver before multi-driver discount · 3 months · limited spots
                        </p>
                      )}
                    </div>

                    {/* Number of Drivers */}
                    <div className="flex flex-col">
                      <label className="flex items-center gap-2 text-navy font-medium mb-2">
                        <Car size={18} />
                        How many drivers do you want for your campaign?
                      </label>
                      <p className="text-xs text-navy/50 mb-2" data-testid="campaign-driver-limit-note">
                        Up to {MAX_CAMPAIGN_DRIVERS} drivers per campaign. We'll match you with available drivers in your area.
                      </p>
                      <select
                        name="driverCount"
                        value={advertiserData.driverCount}
                        onChange={handleAdvertiserChange}
                        required
                        className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors bg-white"
                        data-testid="driver-count-select"
                      >
                        <option value="">Select number of drivers</option>
                        {CAMPAIGN_DRIVER_OPTIONS.map(n => (
                          <option key={n} value={n}>{n} driver{n === 1 ? '' : 's'}</option>
                        ))}
                      </select>
                      <p className="text-xs text-emerald-700 mt-1" data-testid="campaign-driver-discount-note">
                        5% off the total per additional driver — up to 25% off with 6 drivers.
                      </p>
                    </div>
                  </div>

                  {/* Instagram handle — powers Bloom Attribution */}
                  <div className="mt-6">
                    <label className="flex items-center gap-2 text-navy font-medium mb-2">
                      <span className="text-pink-600">✿</span>
                      Instagram handle
                      <span className="text-xs font-normal text-navy/50">(optional — powers Bloom Attribution)</span>
                    </label>
                    <p className="text-xs text-navy/50 mb-2">
                      Every campaign includes a monthly $100 Bloom Attribution sweepstakes for
                      people who follow your account and comment on your pinned post. Add your
                      handle now and we'll auto-track follower growth as attribution.
                    </p>
                    <div className="flex items-center border-2 border-navy/20 rounded-xl focus-within:border-navy transition-colors bg-white overflow-hidden">
                      <span className="px-3 text-navy/40 font-semibold">@</span>
                      <input
                        type="text"
                        name="instagramHandle"
                        value={advertiserData.instagramHandle}
                        onChange={handleAdvertiserChange}
                        placeholder="yourbusiness"
                        className="flex-1 py-3 pr-4 focus:outline-none"
                        data-testid="advertiser-instagram-handle"
                      />
                    </div>
                  </div>

                  {/* Wrap Type Previews */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    <div>
                      <CarPreview type="full_wrap" />
                      <div className="p-4 text-center">
                        <h4 className="font-bold text-navy">Full Wrap</h4>
                        <p className="text-navy/60 text-sm mt-1">Entire vehicle covered in your branded design for maximum visibility.</p>
                      </div>
                    </div>
                    <div>
                      <CarPreview type="partial_wrap" />
                      <div className="p-4 text-center">
                        <h4 className="font-bold text-navy">Partial Wrap</h4>
                        <p className="text-navy/60 text-sm mt-1">Your logo and branding on select panels — subtle yet effective.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center pt-6">
                  <button
                    type="submit"
                    disabled={isSubmittingAdvertiser || miniZones.length === 0 || !advertiserData.driverCount || !advertiserData.wrapType || !advertiserData.campaignDuration}
                    className={`inline-flex items-center gap-3 px-10 py-5 rounded-full text-xl font-bold transition-all ${
                      isSubmittingAdvertiser || miniZones.length === 0 || !advertiserData.driverCount || !advertiserData.wrapType || !advertiserData.campaignDuration
                        ? 'bg-navy/50 text-cream/70 cursor-not-allowed'
                        : 'bg-navy text-cream hover:bg-navy-dark pulse-cta'
                    }`}
                    data-testid="submit-advertiser-form"
                  >
                    <Send size={24} />
                    {isSubmittingAdvertiser ? 'Submitting...' : 'Submit Campaign Request'}
                  </button>
                  <p className="text-sm text-navy/60 mt-3">
                    We'll review your request and be in touch soon.
                  </p>
                </div>
              </form>
            )}
          </div>
        </section>
      )}

      {/* Driver Form */}
      {activeTab === 'driver' && (
        <section className="py-12 bg-white" data-testid="driver-form-section">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-navy mb-4">Driver Application</h2>
              <p className="text-navy/70 text-lg">Sign up as a driver on the Advectigroup app (iOS/Android).</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LeadForm;
